/**
 * StudentConcessionsDialog — Phase 19B (lightweight admin UI).
 *
 * Lets school admins view, add, and void per-student fee concessions:
 *   • Fixed discount   (₹ off)
 *   • Percent discount (% off)
 *   • Waiver           (100% off)
 *   • Custom amount    (set exact payable)
 *
 * Plus a one-click "Mark as RTE" shortcut that creates a waiver across all
 * heads with `reason_code=rte`.
 *
 * This dialog is purely an ADMIN-side workflow. Teacher flow and collection
 * enforcement are unchanged.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Plus,
  Prohibit,
  ShieldCheck,
  Tag,
  X,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const KIND_LABEL = {
  fixed_discount: "Fixed discount (₹)",
  percent_discount: "Percent discount (%)",
  waiver: "Waiver (100% off)",
  custom_amount: "Custom amount (₹)",
};

const REASON_LABEL = {
  rte: "RTE",
  sibling: "Sibling discount",
  staff_child: "Staff child",
  scholarship: "Scholarship",
  merit: "Merit",
  negotiated: "Negotiated",
  other: "Other",
};

export default function StudentConcessionsDialog({ open, onOpenChange, student, onChanged }) {
  const [items, setItems] = useState([]);
  const [structures, setStructures] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [form, setForm] = useState(newForm());
  const [siblings, setSiblings] = useState([]);
  const [siblingPicks, setSiblingPicks] = useState({});  // {student_id: true}

  const studentId = student?.id;

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [c, sum] = await Promise.all([
        api.get(`/school/concessions?student_id=${studentId}&include_voided=true&limit=100`),
        api.get(`/school/students/${studentId}/fee-summary`).catch(() => ({ data: null })),
      ]);
      setItems(c.data.items || []);
      setSummary(sum.data);
      if (sum.data?.student) {
        const { class_id, session_id } = sum.data.student;
        // Pull fee_structures so the admin can target a specific head
        const fs = await api.get(
          `/school/fee-structures?class_id=${class_id}&session_id=${session_id}`,
        );
        setStructures(fs.data || []);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, [studentId]);

  useEffect(() => {
    if (open) {
      setAdding(false);
      setForm(newForm());
      setSiblings([]);
      setSiblingPicks({});
      load();
    }
  }, [open, load]);

  // Fetch siblings whenever the user opens "Add concession" with reason=sibling
  useEffect(() => {
    if (!adding || !studentId) return;
    if (form.reason_code !== "sibling") {
      setSiblings([]);
      setSiblingPicks({});
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/school/students/${studentId}/siblings`);
        if (!cancel) {
          setSiblings(r.data.items || []);
          // Preselect all detected siblings by default
          const picks = {};
          (r.data.items || []).forEach((s) => { picks[s.id] = true; });
          setSiblingPicks(picks);
        }
      } catch {
        if (!cancel) { setSiblings([]); setSiblingPicks({}); }
      }
    })();
    return () => { cancel = true; };
  }, [adding, studentId, form.reason_code]);

  const hasActiveRte = items.some(
    (it) => !it.is_voided && it.reason_code === "rte" && it.kind === "waiver" && !it.fee_structure_id,
  );

  const markRte = async () => {
    if (!studentId) return;
    setSubmitting(true);
    try {
      await api.post("/school/concessions", {
        student_id: studentId,
        kind: "waiver",
        value: 0,
        reason_code: "rte",
        note: "Marked as RTE student — full fee waiver across all heads.",
      });
      toast.success("Student marked as RTE");
      onChanged?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!studentId) return;
    setSubmitting(true);
    try {
      const body = {
        student_id: studentId,
        kind: form.kind,
        value: form.kind === "waiver" ? 0 : Number(form.value),
        reason_code: form.reason_code,
        note: form.note.trim(),
        fee_structure_id: form.fee_structure_id || null,
        period_month: form.period_month || null,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
      };
      await api.post("/school/concessions", body);
      // Apply same concession to picked siblings (best-effort; collect failures
      // so the admin sees a summary instead of partial silent fails).
      const picked = form.reason_code === "sibling"
        ? siblings.filter((s) => siblingPicks[s.id])
        : [];
      let applied = 0;
      const failures = [];
      for (const sib of picked) {
        try {
          await api.post("/school/concessions", { ...body, student_id: sib.id });
          applied += 1;
        } catch (err) {
          failures.push(`${sib.name}: ${formatApiError(err)}`);
        }
      }
      if (applied > 0) {
        toast.success(`Concession added (${applied + 1} students)`);
      } else {
        toast.success("Concession added");
      }
      if (failures.length) {
        toast.warning(
          `Could not apply to ${failures.length} sibling(s): ${failures.join("; ")}`,
        );
      }
      setAdding(false);
      setForm(newForm());
      setSiblings([]);
      setSiblingPicks({});
      onChanged?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  const submitVoid = async (concessionId) => {
    if (!voidReason || voidReason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/school/concessions/${concessionId}/void`, { reason: voidReason.trim() });
      toast.success("Concession voided");
      setVoidingId(null);
      setVoidReason("");
      onChanged?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="student-concessions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Tag size={20} weight="duotone" /> Concessions — {student?.name}
          </DialogTitle>
          <DialogDescription>
            Per-student fee adjustments. Concession-aware amounts are enforced at
            collection — teachers cannot collect more than the net payable.
          </DialogDescription>
        </DialogHeader>

        {/* Frequency-aware summary strip — Phase 19C */}
        {summary?.totals && (() => {
          const heads = summary.heads || [];
          const monthly = heads.filter((h) => h.frequency_normalized === "monthly");
          const annual = heads.filter((h) => h.frequency_normalized !== "monthly");
          const sum = (rows, k) => rows.reduce((a, h) => a + (h[k] || 0), 0);
          const blocks = [];
          if (annual.length) {
            blocks.push({
              key: "annual",
              prefix: "Annual",
              suffix: "",
              base: sum(annual, "amount"),
              concession: sum(annual, "concession"),
              effective: sum(annual, "effective_amount"),
            });
          }
          if (monthly.length) {
            blocks.push({
              key: "monthly",
              prefix: "Monthly",
              suffix: "/ month",
              base: sum(monthly, "amount"),
              concession: sum(monthly, "concession"),
              effective: sum(monthly, "effective_amount"),
            });
          }
          if (blocks.length === 0) return null;
          return (
            <div className="space-y-2">
              {blocks.map((b) => (
                <div key={b.key} className="grid grid-cols-3 gap-2 text-xs">
                  <Stat
                    label={`${b.prefix} base ${b.suffix}`.trim()}
                    value={formatINR(b.base)}
                  />
                  <Stat
                    label={`Concession ${b.suffix}`.trim()}
                    value={formatINR(b.concession)}
                    tone={b.concession > 0 ? "amber" : "muted"}
                    testid={`concessions-total-${b.key}`}
                  />
                  <Stat
                    label={`Net payable ${b.suffix}`.trim()}
                    value={formatINR(b.effective)}
                    tone="primary"
                    testid={`effective-total-${b.key}`}
                  />
                </div>
              ))}
            </div>
          );
        })()}

        {/* Quick RTE toggle + Add button */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {!hasActiveRte ? (
            <Button
              variant="outline"
              onClick={markRte}
              disabled={submitting}
              className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-700"
              data-testid="mark-rte-btn"
            >
              <ShieldCheck size={14} weight="duotone" /> Mark as RTE student
            </Button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-md" data-testid="rte-active-chip">
              <ShieldCheck size={13} weight="fill" /> RTE — full fee waived
            </span>
          )}
          <div className="flex-1" />
          {!adding && (
            <Button onClick={() => setAdding(true)} className="gap-1.5" data-testid="add-concession-btn">
              <Plus size={14} weight="bold" /> Add concession
            </Button>
          )}
        </div>

        {/* Add form (collapsible) */}
        {adding && (
          <form onSubmit={submit} className="space-y-3 border border-slate-200 rounded-md p-3 bg-slate-50/60" data-testid="add-concession-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                  <SelectTrigger data-testid="concession-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(KIND_LABEL).map(([k, l]) => (
                      <SelectItem key={k} value={k}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reason">
                <Select value={form.reason_code} onValueChange={(v) => setForm((f) => ({ ...f, reason_code: v }))}>
                  <SelectTrigger data-testid="concession-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASON_LABEL).map(([k, l]) => (
                      <SelectItem key={k} value={k}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {form.kind !== "waiver" && (
                <Field label={form.kind === "percent_discount" ? "Value (%)" : "Value (₹)"}>
                  <Input
                    type="number"
                    min={form.kind === "percent_discount" ? 0.01 : 0}
                    max={form.kind === "percent_discount" ? 100 : undefined}
                    step="0.01"
                    value={form.value}
                    onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                    data-testid="concession-value"
                  />
                </Field>
              )}
              <Field label="Fee head (optional)">
                <Select
                  value={form.fee_structure_id || "__all__"}
                  onValueChange={(v) => setForm((f) => ({ ...f, fee_structure_id: v === "__all__" ? "" : v }))}
                >
                  <SelectTrigger data-testid="concession-head"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All heads</SelectItem>
                    {structures.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.head} ({formatINR(s.amount)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Effective from (optional)">
                <Input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
                  data-testid="concession-from"
                />
              </Field>
              <Field label="Effective to (optional)">
                <Input
                  type="date"
                  value={form.effective_to}
                  onChange={(e) => setForm((f) => ({ ...f, effective_to: e.target.value }))}
                  data-testid="concession-to"
                />
              </Field>
            </div>
            {form.reason_code === "sibling" && siblings.length > 0 && (
              <div className="border border-emerald-200 rounded-md bg-emerald-50/50 p-2.5 space-y-1.5" data-testid="sibling-picker">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
                  Apply to {siblings.length} sibling{siblings.length > 1 ? "s" : ""} sharing parent's phone
                </div>
                {siblings.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`sibling-pick-${s.id}`}>
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={!!siblingPicks[s.id]}
                      onChange={(e) => setSiblingPicks((p) => ({ ...p, [s.id]: e.target.checked }))}
                    />
                    <span className="font-medium text-slate-900">{s.name}</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-500">{s.admission_no}</span>
                    {s.class_name && (
                      <>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-500">Class {s.class_name}</span>
                      </>
                    )}
                  </label>
                ))}
              </div>
            )}
            <Field label="Note (≥ 3 chars)">
              <Textarea
                rows={2}
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="e.g. Sibling of Rahul Sharma · Approved by Principal"
                data-testid="concession-note"
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAdding(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-concession-btn">
                {submitting ? "Saving…" : "Save concession"}
              </Button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <div className="max-h-72 overflow-auto">
            {loading ? (
              <div className="py-6 text-center text-sm text-slate-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500" data-testid="no-concessions">
                No concessions yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Type · Value</th>
                    <th className="px-3 py-2 text-left">Scope</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-left">Note</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((it) => (
                    <tr key={it.id} className={it.is_voided ? "opacity-50" : ""} data-testid={`concession-row-${it.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">
                          {KIND_LABEL[it.kind] || it.kind}
                        </div>
                        <div className="text-xs text-slate-500">
                          {it.kind === "waiver" ? "—" :
                           it.kind === "percent_discount" ? `${it.value}%` :
                           formatINR(it.value)}
                          {it.is_voided && <span className="ml-2 text-red-700 font-semibold">VOIDED</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{it.head || "All heads"}</div>
                        <div className="text-slate-400">{it.period_month || "All months"}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                          {REASON_LABEL[it.reason_code] || it.reason_code}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 max-w-xs truncate">{it.note}</td>
                      <td className="px-3 py-2 text-right">
                        {!it.is_voided && (
                          <button
                            type="button"
                            onClick={() => { setVoidingId(it.id); setVoidReason(""); }}
                            className="text-red-700 hover:bg-red-50 p-1 rounded"
                            title="Void"
                            data-testid={`void-concession-${it.id}`}
                          >
                            <Prohibit size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </DialogContent>

      <AlertDialog open={!!voidingId} onOpenChange={(o) => !o && setVoidingId(null)}>
        <AlertDialogContent data-testid="concession-void-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <Prohibit size={18} /> Void concession?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The concession will be marked as voided but preserved for audit history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason (≥ 3 chars)</Label>
            <Input
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Mistake — wrong student"
              data-testid="void-concession-reason"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitVoid(voidingId); }}
              disabled={submitting}
              className="bg-red-700 hover:bg-red-800"
              data-testid="void-concession-confirm-btn"
            >
              {submitting ? "Voiding…" : "Yes, void"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function newForm() {
  return {
    kind: "fixed_discount",
    value: "",
    reason_code: "sibling",
    note: "",
    fee_structure_id: "",
    period_month: "",
    effective_from: "",
    effective_to: "",
  };
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "muted", testid }) {
  const toneCls = {
    primary: "bg-indigo-50 border-indigo-200 text-indigo-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    muted: "bg-slate-50 border-slate-200 text-slate-700",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div className={`border rounded-md px-3 py-2 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
